import { makeAutoObservable, reaction, runInAction } from "mobx";
import { makePersistable } from "mobx-persist-store";
import { API_BASE_URL } from "@/shared/config/config";

export type SessionCounterMode = "auto" | "manual";
export type SessionCounterViewMode = "detailed" | "row";
export type SessionCounterMetricMode = "profit" | "winrate";
type LegacySessionCounterViewMode = SessionCounterViewMode | "no-bg";

export type SessionCounterStatsSnapshot = {
  wins: number;
  losses: number;
  total: number;
  pnl: number;
  winRate: number;
  elapsedSeconds: number;
  timezone?: string;
  accountLabel: string;
  pairSymbol: string;
  pairIcon: string | null;
  pairIsOtc: boolean;
};

export type SessionCounterShareState = {
  startedAt: string | null;
  minimized: boolean;
  mode: SessionCounterMode;
  viewMode: SessionCounterViewMode;
  metricMode: SessionCounterMetricMode;
  timezone?: string;
  footerExpanded: boolean;
  manualWinsDelta: number;
  manualLossesDelta: number;
  transparentBackground: boolean;
  transparency: number;
  windowWidth: number;
  windowHeight: number;
};

type SessionCounterSyncState = {
  enabled: boolean;
  minimized: boolean;
  transparency: number;
  startedAt: string | null;
  mode: SessionCounterMode;
  viewMode: SessionCounterViewMode;
  metricMode: SessionCounterMetricMode;
  timezone?: string;
  footerExpanded: boolean;
  manualWinsDelta: number;
  manualLossesDelta: number;
  transparentBackground: boolean;
  windowWidth: number;
  windowHeight: number;
  statsSnapshot: SessionCounterStatsSnapshot | null;
};

type LegacySessionCounterSyncState = Omit<SessionCounterSyncState, "viewMode"> & {
  viewMode: LegacySessionCounterViewMode;
};

type LegacySessionCounterShareState = Omit<SessionCounterShareState, "viewMode"> & {
  viewMode: LegacySessionCounterViewMode;
};

type SessionCounterRemoteDataState = Pick<
  SessionCounterSyncState,
  "enabled" | "startedAt" | "timezone" | "manualWinsDelta" | "manualLossesDelta" | "statsSnapshot"
>;

const SESSION_COUNTER_SYNC_KEY = "SessionCounterWidgetSync";
const SESSION_COUNTER_SYNC_CHANNEL = "session-counter-widget";
const IS_WIDGET_PAGE = typeof window !== "undefined" && window.location.pathname.includes("/session-counter-widget");

const toPlainSyncState = (state: SessionCounterSyncState): SessionCounterSyncState => {
  return JSON.parse(JSON.stringify(state)) as SessionCounterSyncState;
};

const toPlainRemoteDataState = (state: SessionCounterRemoteDataState): SessionCounterRemoteDataState => {
  return JSON.parse(JSON.stringify(state)) as SessionCounterRemoteDataState;
};

const normalizeTransparency = (value: number | undefined) => {
  if (value === undefined || !Number.isFinite(value) || value >= 100) return 0;
  return Math.min(99, Math.max(0, value));
};

const normalizeViewMode = (value: LegacySessionCounterViewMode | undefined): SessionCounterViewMode => {
  return value === "row" ? "row" : "detailed";
};

const normalizeTimezone = (value: string | undefined): string => {
  const normalized = value?.trim();
  return normalized || "UTC";
};

class SessionCounterStore {
  enabled = false;
  minimized = false;
  transparency = 0;
  startedAt: string | null = null;
  mode: SessionCounterMode = "auto";
  viewMode: SessionCounterViewMode = "detailed";
  metricMode: SessionCounterMetricMode = "profit";
  timezone = "UTC";
  footerExpanded = true;
  manualWinsDelta = 0;
  manualLossesDelta = 0;
  transparentBackground = false;
  windowWidth = 330;
  windowHeight = 265;
  statsSnapshot: SessionCounterStatsSnapshot | null = null;
  private applyingExternalState = false;
  private syncChannel: BroadcastChannel | null = null;
  private outboundSyncEnabled = !IS_WIDGET_PAGE;
  private remoteSyncTimeout: number | null = null;
  private remoteSessionId: string | null = null;

  constructor() {
    makeAutoObservable(this, {
      syncChannel: false,
      outboundSyncEnabled: false,
      remoteSyncTimeout: false,
      remoteSessionId: false,
      setupExternalSync: false,
      setupLegacyViewModeMigration: false,
      applyExternalState: false,
      setOutboundSyncEnabled: false,
      scheduleRemoteSync: false,
      publishRemoteSyncState: false,
      publishSyncStateNow: false,
      clearRemoteSync: false,
    } as Record<string, false>);

    if (!IS_WIDGET_PAGE) {
      makePersistable(this, {
        name: "SessionCounterStore",
        properties: [
          "enabled",
          "minimized",
          "transparency",
          "startedAt",
          "mode",
          "viewMode",
          "metricMode",
          "timezone",
          "footerExpanded",
          "manualWinsDelta",
          "manualLossesDelta",
          "transparentBackground",
          "windowWidth",
          "windowHeight",
          "statsSnapshot",
        ],
        storage: localStorage,
      });
    }

    this.setupExternalSync();
    this.setupLegacyViewModeMigration();
  }

  get syncState(): SessionCounterSyncState {
    return {
      enabled: this.enabled,
      minimized: this.minimized,
      transparency: this.transparency,
      startedAt: this.startedAt,
      mode: this.mode,
      viewMode: normalizeViewMode(this.viewMode),
      metricMode: this.metricMode,
      timezone: normalizeTimezone(this.timezone),
      footerExpanded: this.footerExpanded,
      manualWinsDelta: this.manualWinsDelta,
      manualLossesDelta: this.manualLossesDelta,
      transparentBackground: this.transparentBackground,
      windowWidth: this.windowWidth,
      windowHeight: this.windowHeight,
      statsSnapshot: this.statsSnapshot,
    };
  }

  get shareState(): SessionCounterShareState {
    return {
      startedAt: this.startedAt,
      minimized: this.minimized,
      mode: this.mode,
      viewMode: normalizeViewMode(this.viewMode),
      metricMode: this.metricMode,
      timezone: normalizeTimezone(this.timezone),
      footerExpanded: this.footerExpanded,
      manualWinsDelta: this.manualWinsDelta,
      manualLossesDelta: this.manualLossesDelta,
      transparentBackground: this.transparentBackground,
      transparency: this.transparency,
      windowWidth: this.windowWidth,
      windowHeight: this.windowHeight,
    };
  }

  get remoteDataState(): SessionCounterRemoteDataState {
    return {
      enabled: this.enabled,
      startedAt: this.startedAt,
      timezone: normalizeTimezone(this.timezone),
      manualWinsDelta: this.manualWinsDelta,
      manualLossesDelta: this.manualLossesDelta,
      statsSnapshot: this.statsSnapshot,
    };
  }

  get obsStreamId(): string | null {
    return this.remoteSessionId ?? this.startedAt;
  }

  private setupExternalSync() {
    if (typeof window === "undefined") return;

    if (IS_WIDGET_PAGE) return;

    this.syncChannel = "BroadcastChannel" in window ? new BroadcastChannel(SESSION_COUNTER_SYNC_CHANNEL) : null;
    this.syncChannel?.addEventListener("message", (event: MessageEvent<SessionCounterSyncState>) => {
      this.applyExternalState(event.data);
    });

    window.addEventListener("storage", (event) => {
      if (event.key !== SESSION_COUNTER_SYNC_KEY || !event.newValue) return;
      try {
        this.applyExternalState(JSON.parse(event.newValue) as SessionCounterSyncState);
      } catch {
        // Ignore malformed external widget state.
      }
    });

    const saved = localStorage.getItem(SESSION_COUNTER_SYNC_KEY);
    if (saved) {
      try {
        this.applyExternalState(JSON.parse(saved) as SessionCounterSyncState);
      } catch {
        localStorage.removeItem(SESSION_COUNTER_SYNC_KEY);
      }
    }

    reaction(
      () => this.syncState,
      (state) => {
        if (this.applyingExternalState || !this.outboundSyncEnabled) return;
        const payload = toPlainSyncState(state);
        localStorage.setItem(SESSION_COUNTER_SYNC_KEY, JSON.stringify(payload));
        this.syncChannel?.postMessage(payload);
      },
      { delay: 50 },
    );

    reaction(
      () => this.remoteDataState,
      (state) => {
        if (this.applyingExternalState || !this.outboundSyncEnabled) return;
        const payload = toPlainRemoteDataState(state);
        this.scheduleRemoteSync(payload);
      },
      { delay: 350 },
    );
  }

  private setupLegacyViewModeMigration() {
    reaction(
      () => this.viewMode as LegacySessionCounterViewMode,
      (viewMode) => {
        if (viewMode !== "no-bg") return;

        runInAction(() => {
          this.transparentBackground = true;
          this.viewMode = "detailed";
        });
      },
      { fireImmediately: true },
    );
  }

  private scheduleRemoteSync(state: SessionCounterRemoteDataState) {
    if (typeof window === "undefined") return;

    const payload = toPlainRemoteDataState(state);
    if (!payload.enabled || !payload.startedAt) {
      this.clearRemoteSync();
      return;
    }

    if (!this.remoteSessionId) {
      this.remoteSessionId = payload.startedAt;
    }

    if (this.remoteSyncTimeout) {
      window.clearTimeout(this.remoteSyncTimeout);
    }

    this.remoteSyncTimeout = window.setTimeout(() => {
      this.remoteSyncTimeout = null;
      this.publishRemoteSyncState(payload);
    }, 350);
  }

  private publishRemoteSyncState(state: SessionCounterRemoteDataState) {
    if (typeof window === "undefined") return;

    if (!state.enabled || !state.startedAt) return;
    const sessionId = this.remoteSessionId ?? state.startedAt;

    const url = new URL(`${API_BASE_URL}/rest/public/session-counter-widget/state`, window.location.origin);
    url.searchParams.set("startedAt", sessionId);

    void fetch(url.toString(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(toPlainRemoteDataState(state)),
    }).catch(() => {});
  }

  publishSyncStateNow() {
    if (this.remoteSyncTimeout) {
      window.clearTimeout(this.remoteSyncTimeout);
      this.remoteSyncTimeout = null;
    }

    const payload = toPlainRemoteDataState(this.remoteDataState);
    if (!payload.enabled || !payload.startedAt) {
      this.clearRemoteSync();
      return;
    }

    if (!this.remoteSessionId) {
      this.remoteSessionId = payload.startedAt;
    }
    this.publishRemoteSyncState(payload);
  }

  private clearRemoteSync() {
    if (typeof window === "undefined") return;

    if (this.remoteSyncTimeout) {
      window.clearTimeout(this.remoteSyncTimeout);
      this.remoteSyncTimeout = null;
    }
    this.remoteSessionId = null;
  }

  setOutboundSyncEnabled(enabled: boolean) {
    this.outboundSyncEnabled = enabled;
  }

  private applyExternalState(state: LegacySessionCounterSyncState) {
    if (!state || this.applyingExternalState) return;

    this.applyingExternalState = true;
    runInAction(() => {
      this.enabled = state.enabled;
      if (state.minimized !== undefined) this.minimized = state.minimized;
      this.transparency = normalizeTransparency(state.transparency);
      this.startedAt = state.startedAt;
      this.mode = state.mode;
      this.transparentBackground = state.transparentBackground ?? state.viewMode === "no-bg";
      this.viewMode = normalizeViewMode(state.viewMode);
      this.metricMode = state.metricMode;
      this.timezone = normalizeTimezone(state.timezone);
      this.footerExpanded = state.footerExpanded;
      this.manualWinsDelta = state.manualWinsDelta;
      this.manualLossesDelta = state.manualLossesDelta;
      this.windowWidth = state.windowWidth;
      this.windowHeight = state.windowHeight;
      this.statsSnapshot = state.statsSnapshot;
    });
    queueMicrotask(() => {
      this.applyingExternalState = false;
    });
  }

  applyShareState(state: Partial<LegacySessionCounterShareState>) {
    if (!state) return;

    this.statsSnapshot = null;

    if (state.startedAt !== undefined) this.startedAt = state.startedAt;
    if (state.minimized !== undefined) this.minimized = state.minimized;
    if (state.mode !== undefined) this.mode = state.mode;
    if (state.transparentBackground !== undefined) this.transparentBackground = state.transparentBackground;
    if (state.viewMode === "no-bg") this.transparentBackground = true;
    if (state.viewMode !== undefined) this.viewMode = normalizeViewMode(state.viewMode);
    if (state.metricMode !== undefined) this.metricMode = state.metricMode;
    if (state.timezone !== undefined) this.timezone = normalizeTimezone(state.timezone);
    if (state.footerExpanded !== undefined) this.footerExpanded = state.footerExpanded;
    if (state.manualWinsDelta !== undefined) this.manualWinsDelta = state.manualWinsDelta;
    if (state.manualLossesDelta !== undefined) this.manualLossesDelta = state.manualLossesDelta;
    if (state.transparency !== undefined) this.transparency = normalizeTransparency(state.transparency);
    if (state.windowWidth !== undefined) this.windowWidth = state.windowWidth;
    if (state.windowHeight !== undefined) this.windowHeight = state.windowHeight;
  }

  applyRemoteSyncState(state: SessionCounterSyncState) {
    this.applyExternalState(state);
  }

  applyRemoteDataState(state: SessionCounterSyncState) {
    if (!state || this.applyingExternalState) return;

    this.applyingExternalState = true;
    runInAction(() => {
      this.enabled = state.enabled;
      this.startedAt = state.startedAt;
      this.timezone = normalizeTimezone(state.timezone);
      this.manualWinsDelta = state.manualWinsDelta;
      this.manualLossesDelta = state.manualLossesDelta;
      this.statsSnapshot = state.statsSnapshot;
    });
    queueMicrotask(() => {
      this.applyingExternalState = false;
    });
  }

  setEnabled(enabled: boolean) {
    runInAction(() => {
      this.enabled = enabled;
      if (!enabled) {
        this.startedAt = null;
        this.minimized = false;
        this.clearRemoteSync();
      } else {
        this.minimized = false;
        this.transparency = normalizeTransparency(this.transparency);
      }
    });
  }

  setMinimized(minimized: boolean) {
    runInAction(() => {
      this.minimized = minimized;
    });
  }

  setTransparency(value: number) {
    runInAction(() => {
      this.transparency = Math.min(99, Math.max(0, value));
    });
  }

  setMode(mode: SessionCounterMode) {
    runInAction(() => {
      this.mode = mode;
    });
  }

  setViewMode(viewMode: SessionCounterViewMode) {
    runInAction(() => {
      this.viewMode = normalizeViewMode(viewMode);
    });
  }

  setTransparentBackground(enabled: boolean) {
    runInAction(() => {
      this.transparentBackground = enabled;
    });
  }

  setMetricMode(metricMode: SessionCounterMetricMode) {
    runInAction(() => {
      this.metricMode = metricMode;
    });
  }

  setTimezone(timezone: string | undefined) {
    runInAction(() => {
      this.timezone = normalizeTimezone(timezone);
    });
  }

  setFooterExpanded(expanded: boolean) {
    runInAction(() => {
      this.footerExpanded = expanded;
    });
  }

  incrementManualWinsDelta() {
    runInAction(() => {
      this.manualWinsDelta += 1;
    });
  }

  decrementManualWinsDelta() {
    runInAction(() => {
      this.manualWinsDelta -= 1;
    });
  }

  incrementManualLossesDelta() {
    runInAction(() => {
      this.manualLossesDelta += 1;
    });
  }

  decrementManualLossesDelta() {
    runInAction(() => {
      this.manualLossesDelta -= 1;
    });
  }

  resetManualDeltas() {
    runInAction(() => {
      this.manualWinsDelta = 0;
      this.manualLossesDelta = 0;
    });
  }

  setWindowSize(width: number, height: number) {
    runInAction(() => {
      this.windowWidth = width;
      this.windowHeight = height;
    });
  }

  setStatsSnapshot(snapshot: SessionCounterStatsSnapshot) {
    runInAction(() => {
      this.statsSnapshot = snapshot;
    });
  }

  start() {
    runInAction(() => {
      this.startedAt = new Date().toISOString();
      if (!this.remoteSessionId) {
        this.remoteSessionId = this.startedAt;
      }
    });
  }

  restart() {
    runInAction(() => {
      this.startedAt = new Date().toISOString();
      if (!this.remoteSessionId) {
        this.remoteSessionId = this.startedAt;
      }
    });
  }

}

export const sessionCounterStore = new SessionCounterStore();
